import { StrictMode, Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from './lib/queryClient'
import './index.css'
import './i18n' // Initialize i18n
import { useThemeStore } from './store/useThemeStore'
import App from './App.tsx'
import { darfAutomatischNeuladen } from './utils/chunkNeuladen'
import { bereinigeAmazonAssistentAltbestand, loescheAmazonAssistentStand } from './utils/amazonAssistentSpeicher'
import { api } from './api/client'

// V-2093: Klartext-Altbestand des Amazon-Assistenten entfernen, auch ohne Besuch der Amazon-Unterseite
bereinigeAmazonAssistentAltbestand()
// V-2093 F-003/F-006: Der Zwischenstand gehört keinem Folgebenutzer — weg bei Abmelden, Benutzer- UND
// Mandantenwechsel (auch bei Anmeldung über /login ohne vorheriges Abmelden), nicht beim Token-Refresh.
api.onAuthWechsel(loescheAmazonAssistentStand)

// Eigene Subdomain dms.bispicy.com → eigenständige BIS-Archiv-App (Login + Archiv),
// ohne die volle ERP-/Kundencenter-Navigation. Sonst die normale ERP-SPA.
const DmsStandaloneApp = lazy(() => import('./dms/DmsStandaloneApp'))
const isDmsSubdomain = typeof window !== 'undefined' && /^dms\./i.test(window.location.hostname)

// Eigene Subdomain my.bispicy.com → eigenständiges Kundencenter (MyPortal) am Root.
// Wir mounten denselben /my/*-Subtree wie in der ERP-App, damit alle internen
// /my/...-Links/Redirects unverändert gültig bleiben (URL: my.bispicy.com/my/...).
// Greift erst, sobald der DNS-/Ingress-Eintrag für my.bispicy.com existiert — bis
// dahin No-op auf allen Bestands-Hosts.
const MyPortalApp = lazy(() => import('./myportal/MyPortalApp'))
const isMyPortalSubdomain = typeof window !== 'undefined' && /^my\./i.test(window.location.hostname)

// Eigene Subdomain repair.bispicy.com → öffentliche, token-basierte Reparatur-Statusseite
// (kein Login, Token = Pfad). White-Label nach Tenant-Branding. No-op auf Bestands-Hosts.
const RepairStatusApp = lazy(() => import('./repair/RepairStatusApp'))
const isRepairSubdomain = typeof window !== 'undefined' && /^repair\./i.test(window.location.hostname)

// Mitarbeiter-Edit unter repair.bispicy.com/s/{staffToken}: ohne Login Status ändern (Phase 3).
// Eigener Mitarbeiter-Token (QR auf der internen Werkstattkopie), NICHT der Kunden-Lese-Token.
const RepairStaffEditApp = lazy(() => import('./repair/RepairStaffEditApp'))
const isRepairStaffEdit = isRepairSubdomain && /^\/s\//i.test(window.location.pathname)

// Eigene Subdomain admin.bispicy.com → Admin-Bereich der ERP-App. Der Admin-Bereich ist
// kein eigenes Modul, sondern lebt als /admin/*-Routen in der ERP-App. Daher: normale App
// rendern, aber am Root direkt auf /admin leiten (der Guard schickt dann ggf. zu /admin/login).
const isAdminSubdomain = typeof window !== 'undefined' && /^admin\./i.test(window.location.hostname)
if (isAdminSubdomain && window.location.pathname === '/') {
  window.history.replaceState(null, '', '/admin')
}

// Eigene Subdomain pos.bispicy.com → Browser-Kasse (WebPOS). Die WebPOS lebt als
// /pos/*-Routen in der ERP-App (absolute Redirects auf /pos). Daher: normale App
// rendern, aber am Root direkt auf /pos leiten (Guard schickt dann ggf. zu /pos/auth).
const isPosSubdomain = typeof window !== 'undefined' && /^pos\./i.test(window.location.hostname)
if (isPosSubdomain && window.location.pathname === '/') {
  window.history.replaceState(null, '', '/pos')
}

// Läuft die App als installierte PWA (vom Home-Bildschirm, ohne Browser-Chrome)?
// Dann markieren wir das <html> mit .pwa-standalone, damit die WebPOS Safe-Area-
// Insets (Notch/Statusleiste/Home-Indikator) einrechnet und wie eine native App wirkt.
if (typeof window !== 'undefined') {
  const isStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: fullscreen)').matches
  if (isStandalone) {
    document.documentElement.classList.add('pwa-standalone')
  }
}

// Global fetch interceptor: catches 401 from ALL fetch calls (including raw fetch)
// and dispatches auth:unauthorized event so AuthGuard can show SessionExpiredDialog
const originalFetch = window.fetch;
window.fetch = async function (...args: Parameters<typeof fetch>) {
  const response = await originalFetch.apply(this, args);
  if (response.status === 401) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof Request ? args[0].url : '');
    // Only intercept tenant API calls, not admin or external requests.
    // Admin endpoints (/api/admin/*) use a separate auth context and a 401
    // there should not log out the tenant user.
    const isApiCall = url.includes('/api/') || url.startsWith('/api/');
    const isAdminCall = url.includes('/api/admin') || url.includes('/admin/');
    // billing/overview ist eine nicht-kritische Feature-Probe (useFeatureGate), die einen 401
    // selbst gracefully behandelt (Free-Banner statt Crash). Sie darf NIE den globalen Logout
    // auslösen — sonst kann ein veralteter Token einen Login-Loop verursachen.
    const isFeatureProbe = url.includes('/billing/overview');
    // 🚨 Die /mde/*-Endpoints gehören der Lager-App und authentifizieren ausschliesslich ueber
    // `X-POS-Device-Token`. Ein Browser hat den nie — ihr 401 ist dort der NORMALFALL und sagt
    // ueber die angemeldete Sitzung gar nichts aus. Genau das warf den Benutzer aus dem ERP,
    // sobald eine Seite so einen Endpoint anfasste (Einstellungen -> Pick & Pack, 14.08.2026).
    const isDeviceAuthCall = url.includes('/api/mde/');
    // 🚨 /debug/* ist Telemetrie (debugLogger: client-logs, client-config) und akzeptiert nur das
    // Mandanten-Token. In einer Superadmin-Sitzung antwortet sie IMMER 401 — zwei davon kurz
    // hintereinander (z. B. auf der Kunden-Detailseite) meldeten die ganze Admin-Sitzung ab
    // (Live-Befund 23.09.2026). Telemetrie darf nie über die Anmeldung entscheiden.
    const isTelemetryCall = url.includes('/api/debug/');
    if (isApiCall && !isAdminCall && !isFeatureProbe && !isDeviceAuthCall && !isTelemetryCall) {
      // Grund aus dem 401-Body lesen (z. B. session_superseded), für die differenzierte Meldung.
      let reason: string | undefined;
      let code: string | undefined;
      try {
        const body = await response.clone().json();
        reason = body?.error;
        code = body?.code;
      } catch {
        // Body nicht lesbar -> generisch
      }
      // Zweiter Riegel fuer Geraete-Endpoints ausserhalb von /mde/ (z. B. Kellner-Relay):
      // Der Server benennt diesen Fall selbst, statt dass der Client Pfade raten muss.
      if (code !== 'device_token_required') {
        window.dispatchEvent(new CustomEvent('auth:unauthorized', { detail: { reason } }));
      }
    }
  }
  return response;
};

// Handle chunk load failures after deploy (old chunks return 404 → OOM loop)
window.addEventListener('vite:preloadError', (e) => {
  // Höchstens ein automatisches Neuladen je 5 Minuten und Tab (gemeinsame Regel mit
  // der Fehlergrenze). Sonst läuft der Fehler weiter zur Fehlergrenze, die die
  // Hinweisseite mit Knopf zeigt.
  if (darfAutomatischNeuladen()) {
    e.preventDefault();
    window.location.reload();
  }
});

// Initialize theme from localStorage before render to prevent flash
const initializeTheme = () => {
  const stored = localStorage.getItem('theme-storage');
  if (stored) {
    try {
      const { state } = JSON.parse(stored);
      if (state?.theme === 'dark') {
        document.documentElement.classList.add('dark');
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
};
initializeTheme();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {isRepairSubdomain ? (
        <Suspense fallback={null}>
          {isRepairStaffEdit ? <RepairStaffEditApp /> : <RepairStatusApp />}
        </Suspense>
      ) : isDmsSubdomain ? (
        <Suspense fallback={null}>
          <DmsStandaloneApp />
        </Suspense>
      ) : isMyPortalSubdomain ? (
        <Suspense fallback={null}>
          {/* Kundencenter am Root von my.bispicy.com. MyPortalApp liefert eigene
              <Routes> (path="/dashboard" etc.) → läuft direkt am Root ohne /my. */}
          <BrowserRouter basename={import.meta.env.BASE_URL}>
            <MyPortalApp />
          </BrowserRouter>
        </Suspense>
      ) : (
        <App />
      )}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </StrictMode>,
)
